import type { RequestResponse } from '@/shared/services/requestServices'
import { requestService } from '@/shared/services/requestServices'
import { resolveApiUrl } from '@/shared/utils/requestsUtils'
import { handleHttpError } from '@/shared/utils/queryUtils'
import type {
    CreateImportJobPayload,
    DownloadUrlResponse,
    GetUploadUrlsParams,
    ImportJob,
    UploadUrlsResponse,
    WorkflowConfig,
    WorkflowType
} from '../models/importModels'
import { isWorkflowType } from '../models/importModels'
import type { QueryStringInput } from '@hello-aria/query-string/dist/stringify'

type GetImportJobs = {
    companyId: string
    query?: QueryStringInput
}

export const getImportJobsService = async (
    params: GetImportJobs
): Promise<RequestResponse<ImportJob[]> | undefined> => {
    try {
        return await requestService<ImportJob[]>(
            resolveApiUrl('internal.csv-processors.jobs.list', {
                query: {
                    ...params.query,
                    params: { ...params.query?.params, companyId: params.companyId }
                }
            })
        )
    } catch (error) {
        handleHttpError('getImportJobsService', error)
        throw error
    }
}

export const getImportJobByIdService = async (jobId: string): Promise<RequestResponse<ImportJob> | undefined> => {
    try {
        return await requestService<ImportJob>(
            resolveApiUrl('internal.csv-processors.jobs.get', { variables: { jobId } })
        )
    } catch (error) {
        handleHttpError('getImportJobByIdService', error)
        throw error
    }
}

export const createImportJobService = async (
    payload: CreateImportJobPayload
): Promise<RequestResponse<ImportJob> | undefined> => {
    try {
        return await requestService<ImportJob>(resolveApiUrl('internal.csv-processors.jobs.create'), {
            method: 'POST',
            data: payload
        })
    } catch (error) {
        handleHttpError('createImportJobService', error)
        throw error
    }
}

export const getUploadUrlsService = async (
    params: GetUploadUrlsParams
): Promise<RequestResponse<UploadUrlsResponse> | undefined> => {
    try {
        return await requestService<UploadUrlsResponse>(resolveApiUrl('internal.csv-processors.uploadUrls'), {
            method: 'POST',
            data: {
                companyId: params.companyId,
                workflow: params.workflow,
                fileNames: params.fileNames
            }
        })
    } catch (error) {
        handleHttpError('getUploadUrlsService', error)
        throw error
    }
}

export const getDownloadUrlService = async (
    filePath: string
): Promise<RequestResponse<DownloadUrlResponse> | undefined> => {
    try {
        return await requestService<DownloadUrlResponse>(
            resolveApiUrl('internal.csv-processors.downloadUrl', { query: { params: { filePath } } })
        )
    } catch (error) {
        handleHttpError('getDownloadUrlService', error)
        throw error
    }
}

type RawWorkflowConfigsResponse = {
    companyId: string
    workflows: Record<string, Omit<WorkflowConfig, 'id' | 'companyId' | 'workflowType'>>
}

const workflowTemplateUrls: Record<WorkflowType, string> = {
    'create-loans-from-invoices': '/templates/create-loans-from-invoices.csv',
    'create-loans-from-receivables': '/templates/create-loans-from-receivables.csv',
    'link-invoices-to-receivables': '/templates/link-invoices-to-receivables.csv'
}

export const mapRawWorkflowConfigs = (payload: RawWorkflowConfigsResponse): WorkflowConfig[] => {
    return Object.entries(payload.workflows).flatMap(([workflowType, settings]) => {
        if (!isWorkflowType(workflowType)) return []
        return [
            {
                ...settings,
                id: workflowType,
                companyId: payload.companyId,
                workflowType,
                templateUrl: workflowTemplateUrls[workflowType]
            }
        ]
    })
}

export const getWorkflowConfigsService = async (
    companyId: string
): Promise<RequestResponse<WorkflowConfig[]> | undefined> => {
    try {
        const response = await requestService<RawWorkflowConfigsResponse>(
            resolveApiUrl('internal.csv-processors.workflowConfigs', { variables: { companyId } })
        )
        if (!response?.body?.workflows) return undefined

        return { ...response, body: mapRawWorkflowConfigs(response.body) }
    } catch (error) {
        handleHttpError('getWorkflowConfigsService', error)
        throw error
    }
}
